'use client'

import * as client from 'openid-client'

import { Button } from '@vinted/web-ui'
import { MouseEvent } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { toUrlQuery } from '@marketplace-web/shared/utils'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useLocation } from '@marketplace-web/shared/browser-data'
import { navigateToPage } from '@marketplace-web/shared/browser-util'
import { useEnvs } from '@marketplace-web/environment/environment-util'

import { clickEvent } from '_libs/common/event-tracker/events'
import { SIGNUP_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { useRefUrl } from '@marketplace-web/shared/ref-url'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import useAuthModal from '../../hooks/useAuthModal/useAuthModal'
import useSelectTypeUrl from '../../hooks/useSelectTypeUrl'
import { OAUTH2_CLIENT_ID, OAUTH2_CALLBACK_URL } from '../../constants'

const HeaderLoginButton = () => {
  const refUrl = useRefUrl()
  const translate = useTranslate()
  const { track } = useTracking()
  const { openAuthModal } = useAuthModal()
  const isOauth2Enabled = useFeatureSwitch('svc_iam_oauth_web')
  const svcIamOauthUrl = useEnvs('SVC_IAM_OAUTH_URL')
  const { relativeUrl } = useLocation()
  const isNonNativeFlowAbTestEnabled = useAbTest('non_native_flow')?.variant === 'on'
  const { baseUrl } = useLocation()

  const isAuthPageOpen =
    relativeUrl.startsWith('/member/login') ||
    relativeUrl.startsWith('/member/register') ||
    relativeUrl.startsWith('/member/signup')

  const selectTypeUrl = useSelectTypeUrl()

  const getOauth2LoginUrl = async () => {
    const config: client.Configuration = await client.discovery(
      new URL(svcIamOauthUrl || ''),
      OAUTH2_CLIENT_ID,
    )
    const state = client.randomState()

    const params = {
      redirect_uri: `${baseUrl}${OAUTH2_CALLBACK_URL}`,
      scope: 'offline_access user offline',
      state,
    }

    return client.buildAuthorizationUrl(config, params).toString()
  }

  async function handleClick(event: MouseEvent) {
    track(clickEvent({ target: ClickableElement.Login }))

    if (isOauth2Enabled) {
      event.preventDefault()

      const url = await getOauth2LoginUrl()

      navigateToPage(url)

      return
    }

    if (isAuthPageOpen) return

    event.preventDefault()
    openAuthModal()
  }

  return (
    <Button
      text={translate('header.actions.login')}
      size={Button.Size.Small}
      url={`${isNonNativeFlowAbTestEnabled ? selectTypeUrl : SIGNUP_URL}?${toUrlQuery({ ref_url: refUrl })}`}
      onClick={handleClick}
      testId="header--login-button"
    />
  )
}

export default HeaderLoginButton
